import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Badge } from 'react-bootstrap';
import { BsPlusLg, BsPencil, BsTrash, BsToggleOn, BsToggleOff } from 'react-icons/bs';
import userDocumentTypeService from '../services/userDocumentTypeService';
import ResponsiveTable from './ResponsiveTable';
import Spinner from './Spinner';

const UserDocumentType = () => {
    const [documentTypes, setDocumentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [currentItem, setCurrentItem] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        isActive: true
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const data = await userDocumentTypeService.getAll();
            setDocumentTypes(data);
        } catch (error) {
            console.error('Error loading document types:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (mode, item = null) => {
        setModalMode(mode);
        setCurrentItem(item);
        if (item) {
            setFormData({
                name: item.name,
                description: item.description,
                isActive: item.isActive
            });
        } else {
            setFormData({
                name: '',
                description: '',
                isActive: true
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentItem(null);
        setFormData({
            name: '',
            description: '',
            isActive: true
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (modalMode === 'add') {
                await userDocumentTypeService.create(formData);
            } else {
                await userDocumentTypeService.update(currentItem.id, formData);
            }
            handleCloseModal();
            loadData();
        } catch (error) {
            console.error('Error saving document type:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this document type?')) {
            try {
                await userDocumentTypeService.delete(id);
                loadData();
            } catch (error) {
                console.error('Error deleting document type:', error);
            }
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            await userDocumentTypeService.toggleStatus(id);
            loadData();
        } catch (error) {
            console.error('Error toggling document type status:', error);
        }
    };

    const getStatusBadge = (isActive) => {
        return (
            <Badge bg={isActive ? 'success' : 'danger'}>
                {isActive ? 'Active' : 'Inactive'}
            </Badge>
        );
    };

    const columns = [
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
        { 
            key: 'isActive', 
            label: 'Status',
            render: (value) => getStatusBadge(value)
        }
    ];

    const actions = [
        {
            icon: <BsPencil />,
            variant: 'outline-primary',
            onClick: (item) => handleOpenModal('edit', item)
        },
        {
            icon: <BsTrash />,
            variant: 'outline-danger',
            onClick: (item) => handleDelete(item.id)
        },
        {
            icon: (item) => item.isActive ? <BsToggleOn /> : <BsToggleOff />,
            variant: 'outline-secondary',
            onClick: (item) => handleToggleStatus(item.id)
        }
    ];

    if (loading) {
        return <Spinner />;
    }

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Document Types</h2>
                <Button 
                    variant="primary" 
                    onClick={() => handleOpenModal('add')}
                >
                    <BsPlusLg /> Add Document Type
                </Button>
            </div>

            <ResponsiveTable 
                data={documentTypes}
                columns={columns}
                actions={actions}
            />

            <Modal show={showModal} onHide={handleCloseModal}>
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {modalMode === 'add' ? 'Add New' : 'Edit'} Document Type
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Active"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {modalMode === 'add' ? 'Add' : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default UserDocumentType; 